import { forwardRef, useState, useEffect, useRef, MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { FormNode } from '@/utils/api/aboboApiTypes';
import { ResponseValue } from '@/components/SharedTypes';
import BaseQuestionType from './BaseQuestionType';

// Import shadcn components
import { HStack, Input, Label, Combobox, RadioGroup, RadioGroupItem, Checkbox } from '@/components/ui';
import QuestionHeading from '@/components/QuestionHeading';
import QuestionHelperText from '@/components/QuestionHelperText';

interface MultipleChoiceProps {
  node: FormNode;
}

interface FieldState {
  value: ResponseValue[];
  onChange: (value: ResponseValue[]) => void;
  error?: string;
}

export default forwardRef<HTMLDivElement, MultipleChoiceProps>(function MultipleChoice(
  { node },
  ref
) {
  const { t } = useTranslation();
  const [showOther, setShowOther] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Reset showOther state when question changes
  useEffect(() => {
    setShowOther(false);
  }, [node.id]);

  const validateMultipleChoice = (values: ResponseValue[]) => {
    if (node.metadata?.required && (!values || values.length === 0)) {
      return t('questionTypes.multipleChoice.validation.required');
    }
    return true;
  };

  const transformInput = (values: ResponseValue[]) => {
    return values || [];
  };

  const transformOutput = (values: ResponseValue[]) => {
    return Array.isArray(values) ? values : [values];
  };

  const renderCheckbox = (option: string, index: number, field: FieldState) => {
    const isChecked = field.value.some((v) => v.data === option);
    const uniqueId = `checkbox-${node.id}-${index}`;

    return (
      <div key={index} className="flex items-center justify-start gap-4">
        <Checkbox
          id={uniqueId}
          checked={isChecked}
          onCheckedChange={(checked) => {
            if (checked) {
              field.onChange([...field.value, { data: option }]);
            } else {
              field.onChange(field.value.filter((v) => v.data !== option));
            }
          }}
        />
        <Label htmlFor={uniqueId} className="text-lg font-medium text-left">
          {option}
        </Label>
      </div>
    );
  };

  const renderCheckboxGroup = (field: FieldState) => {
    const allowCustomResponse = node.metadata?.allowCustomResponse;
    const hasCustomValue = field.value.some((v) => v.customInput);

    return (
      <div className="space-y-2">
        {node.options.map((option, index) => renderCheckbox(option, index, field))}
        {allowCustomResponse && (
          <>
            <div className="flex items-center justify-start gap-4">
              <Checkbox
                id={`checkbox-${node.id}-other`}
                checked={showOther || hasCustomValue}
                onCheckedChange={(checked) => {
                  setShowOther(!!checked);
                  if (!checked) {
                    // Remove custom value when unchecking "Other"
                    field.onChange(field.value.filter((v) => !v.customInput));
                  }
                }}
              />
              <Label htmlFor={`checkbox-${node.id}-other`} className="text-lg font-medium text-left">
                {t('questionTypes.multipleChoice.other', 'Other')}
              </Label>
            </div>
            {(showOther || hasCustomValue) && (
              <Input
                key={`custom-input-${node.id}`}
                autoComplete="off"
                placeholder={t('questionTypes.multipleChoice.pleaseSpecify')}
                value={field.value.find((v) => v.customInput)?.data || ''}
                onChange={(e) => {
                  const customValue = e.target.value;
                  const filteredValues = field.value.filter((v) => !v.customInput);
                  if (customValue) {
                    field.onChange([...filteredValues, { data: customValue, customInput: true }]);
                  } else {
                    field.onChange(filteredValues);
                  }
                }}
                className="ml-7"
              />
            )}
          </>
        )}
      </div>
    );
  };

  const renderRadio = (option: string, index: number) => {
    const uniqueId = `radio-${node.id}-${index}`;

    return (
      <HStack key={index} className='justify-start'>
        <RadioGroupItem
          value={option}
          id={uniqueId}
        />
        <Label htmlFor={uniqueId} className="text-lg font-medium text-left">
          {option}
        </Label>
      </HStack>
    );
  };

  const renderRadioGroup = (field: FieldState) => {
    const allowCustomResponse = node.metadata?.allowCustomResponse;
    // Determine the RadioGroup value:
    // - If there's a custom input value, select "__other__"
    // - If showOther is true (user clicked Other but hasn't typed yet), select "__other__"
    // - Otherwise use the actual value
    const hasCustomValue = field.value.some((v) => v.customInput);
    const radioValue = hasCustomValue || showOther ? '__other__' : (field.value[0]?.data || '');

    return (
      <div className="space-y-2">
        <RadioGroup
          key={`radio-group-${node.id}`}
          value={radioValue}
          onValueChange={(value) => {
            if (value === '__other__') {
              setShowOther(true);
              // Clear any existing non-custom value
              if (!field.value.some((v) => v.customInput)) {
                field.onChange([]);
              }
            } else {
              setShowOther(false);
              field.onChange([{ data: value }]);
            }
          }}
        >
          {node.options.map((option, index) => renderRadio(option, index))}
          {allowCustomResponse && (
            <HStack className='justify-start'>
              <RadioGroupItem
                value="__other__"
                id={`radio-${node.id}-other`}
              />
              <Label htmlFor={`radio-${node.id}-other`} className="text-lg font-medium text-left">
                {t('questionTypes.multipleChoice.other', 'Other')}
              </Label>
            </HStack>
          )}
        </RadioGroup>
        {allowCustomResponse && showOther && (
          <Input
            key={`custom-input-${node.id}`}
            autoComplete="off"
            placeholder={t('questionTypes.multipleChoice.pleaseSpecify')}
            value={field.value.find((v) => v.customInput)?.data || ''}
            onChange={(e) => {
              const customValue = e.target.value;
              field.onChange(customValue ? [{ data: customValue, customInput: true }] : []);
            }}
            className="ml-7"
          />
        )}
      </div>
    );
  };

  const renderDropdown = (field: FieldState) => {
    const selectedValue = field.value[0]?.data || '';
    
    const options = node.options.map((option) => ({
      value: option,
      label: option,
    }));
    
    return (
      <Combobox
        key={`combobox-${node.id}`}
        options={options}
        value={selectedValue}
        onValueChange={(value) => {
          field.onChange(value ? [{ data: value }] : []);
        }}
        placeholder={t('questionTypes.multipleChoice.selectOption')}
        searchPlaceholder={t('questionTypes.multipleChoice.searchOptions')}
        emptyText={t('questionTypes.multipleChoice.noOptionsFound')}
        className="w-full"
        showNoneOption={true}
        noneLabel={t('questionTypes.multipleChoice.none')}
        containerRef={containerRef}
      />
    );
  };

  return (
    <BaseQuestionType
      key={node.id}
      node={node}
      validate={validateMultipleChoice}
      transform={{
        input: transformInput,
        output: transformOutput,
      }}
    >
      {(field) => (
        <div className="space-y-4" ref={(el) => {
          // Set both refs - the forwarded ref and our container ref
          if (typeof ref === 'function') {
            ref(el);
          } else if (ref) {
            (ref as MutableRefObject<HTMLDivElement | null>).current = el;
          }
          containerRef.current = el;
        }}>
          <QuestionHeading required={node.metadata?.required}>
            {node.body}
          </QuestionHeading>

          {node.helperText && (
            <QuestionHelperText>
              {node.helperText}
            </QuestionHelperText>
          )}

          <div className="flex items-center justify-center">
            {node.metadata?.allowMultipleSelections ? (
              renderCheckboxGroup(field)
            ) : (
              node.options.length > 10 ?
              renderDropdown(field) :
              renderRadioGroup(field)
            )}
          </div>

          {field.error && (
            <p className="text-red-500 text-sm text-center">{field.error}</p>
          )}
        </div>
      )}
    </BaseQuestionType>
  );
});
